import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AlertCircle,
  Copy,
  GripVertical,
  Info,
  Layers,
  MoreHorizontal,
  Pencil,
  Plus,
  Upload,
  X,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { cn } from "../lib/utils";
import { MediaItem } from "../models/Post";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Label } from "./ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

export const MAX_FILE_SIZE = 300 * 1024 * 1024; // WaveGen upload limit: 300 MiB
export const ALLOWED_TYPE_PATTERNS = [
  /^video\/(mp4|quicktime|webm)$/i, // Only mp4, mov (quicktime), webm
  /^image\/(jpeg|png|gif|webp|heic|heif|svg\+xml)$/i, // Only jpg/jpeg, png, gif, webp, heic/heif, svg
];
export const MAX_NUM_FILES = 35;

export type UploadItem = {
  id: string;
  type: string;
  preview: string;
  mediaItem?: MediaItem;
  uploadProgress?: number;
  error?: string;
};

type MediaUploadSectionProps = {
  uploadItems: UploadItem[];
  handleMediaUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  removeUploadItem: (idToRemove: string) => void;
  reorderUploadItems: (dragIndex: number, dropIndex: number) => void;
  selectedIndex: number;
  onSelectItem: (index: number) => void;
  onReplaceItem: (idToReplace: string, file: File) => void;
  onEditItem?: (index: number) => void; // New prop for editing
  onDuplicateItem: (id: string) => void;
};

function ProgressCircle({
  progress,
  size = "lg",
}: {
  progress: number;
  size?: "sm" | "lg";
}) {
  const sizes = {
    sm: {
      container: "h-10 w-10",
      text: "text-xs",
    },
    lg: {
      container: "h-16 w-16",
      text: "text-sm",
    },
  };

  return (
    <div className={cn("relative", sizes[size].container)}>
      <svg className="absolute -rotate-90" viewBox="0 0 36 36">
        <circle
          cx="18"
          cy="18"
          r="16"
          fill="none"
          className="stroke-gray-200"
          strokeWidth="2.5"
        />
        <circle
          cx="18"
          cy="18"
          r="16"
          fill="none"
          className="stroke-blue-500"
          strokeWidth="2.5"
          strokeDasharray={`${progress} 100`}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={cn("text-white font-medium", sizes[size].text)}>
          {Math.round(progress)}%
        </span>
      </div>
    </div>
  );
}

// New Sortable Item Component
function SortableUploadItem({
  item,
  isSelected,
  onSelect,
  onRemove,
  onReplace,
  onEdit,
  onDuplicate,
}: {
  item: UploadItem;
  isSelected: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onReplace: (file: File) => void;
  onEdit?: () => void; // New prop
  onDuplicate: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  const replaceInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      if (!entries || entries.length === 0) return;
    });

    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, []);

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.();
  };

  const handleDuplicateClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDuplicate();
  };

  const handleFileReplaced = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const file = e.target.files?.[0];
    if (file) {
      onReplace(file);
    }
    // Reset the input value to allow replacing with the same file name again
    if (e.target) {
      e.target.value = "";
    }
  };

  const canEdit = item.mediaItem && !item.error && item.uploadProgress === 100;
  const hasOverlays =
    item.mediaItem?.metadata?.overlays &&
    item.mediaItem.metadata.overlays.length > 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "relative aspect-square rounded-lg border overflow-hidden cursor-pointer group",
        isSelected && "ring-2 ring-blue-500 ring-offset-1"
      )}
      onClick={onSelect}
    >
      {item.error ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-50">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <AlertCircle className="h-6 w-6 text-red-500" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">{item.error}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ) : (
        <>
          {item.type.startsWith("video") ? (
            <video
              src={item.preview}
              className="w-full h-full object-cover pointer-events-none"
              muted
            />
          ) : (
            <img
              src={item.preview}
              alt=""
              className="w-full h-full object-cover pointer-events-none"
            />
          )}
          {item.uploadProgress !== undefined && item.uploadProgress < 100 && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <ProgressCircle progress={item.uploadProgress} size="sm" />
            </div>
          )}
        </>
      )}
      <div
        {...attributes}
        {...listeners}
        className="absolute bottom-1 left-1 p-1 bg-black/30 rounded cursor-grab group-hover:opacity-100 opacity-0 transition-opacity"
        aria-label="Drag to reorder"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="h-4 w-4 text-white" />
      </div>

      <div className="absolute top-1 right-1 flex flex-row-reverse items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 w-6 bg-black/40 p-0 text-white hover:bg-black/60"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label="Remove item"
        >
          <X className="h-4 w-4" />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 bg-black/40 p-0 text-white hover:bg-black/60"
              aria-label="More options"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            onClick={(e) => e.stopPropagation()}
            side="bottom"
            align="end"
          >
            <DropdownMenuItem onClick={handleDuplicateClick}>
              <Copy className="mr-2 h-4 w-4" />
              <span>Duplicate</span>
            </DropdownMenuItem>
            {canEdit && onEdit && (
              <DropdownMenuItem onClick={handleEditClick}>
                <Pencil className="mr-2 h-4 w-4" />
                <span>Edit</span>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Overlay Indicator */}
      {hasOverlays && (
        <TooltipProvider>
          <Tooltip delayDuration={100}>
            <TooltipTrigger asChild>
              <div
                className="absolute bottom-1 right-1 h-6 w-6 p-0 bg-black/40 flex items-center justify-center rounded-sm"
                aria-label="Has overlays"
              >
                <Layers className="h-3 w-3 text-white" />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs">This item has overlays</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Edit button - positioned at bottom left */}
      {/* {canEdit && onEdit && (
        <TooltipProvider>
          <Tooltip delayDuration={100}>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute bottom-1 left-1 h-6 w-6 p-0 bg-black/40 hover:bg-black/60 text-white group-hover:opacity-100 opacity-0 transition-opacity"
                onClick={handleEditClick}
                aria-label="Edit media"
              >
                <Pencil className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs">Edit media</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )} */}

      <input
        type="file"
        ref={replaceInputRef}
        className="hidden"
        accept="video/mp4,video/quicktime,video/webm,image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,image/svg+xml"
        onChange={handleFileReplaced}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

export function MediaUploadSection({
  uploadItems,
  handleMediaUpload,
  removeUploadItem,
  reorderUploadItems,
  selectedIndex,
  onSelectItem,
  onReplaceItem,
  onEditItem, // New prop
  onDuplicateItem,
}: MediaUploadSectionProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = uploadItems.findIndex((item) => item.id === active.id);
      const newIndex = uploadItems.findIndex((item) => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        reorderUploadItems(oldIndex, newIndex);
        onSelectItem(newIndex);
      }
    }
  };

  const triggerFileInput = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const input = fileInputRef.current;
    if (input) {
      input.value = "";
      input.click();
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const syntheticEvent = {
      target: {
        files: e.dataTransfer.files,
      },
    } as unknown as React.ChangeEvent<HTMLInputElement>;

    handleMediaUpload(syntheticEvent);
  };

  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      className="hidden"
      accept="video/mp4,video/quicktime,video/webm,image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,image/svg+xml"
      multiple
      onChange={handleMediaUpload}
      onClick={(e) => e.stopPropagation()}
    />
  );

  if (uploadItems.length === 0) {
    return (
      <>
        {fileInput}
        <div
          className={cn(
            "flex items-center justify-center border-2 border-dashed rounded-lg p-12 cursor-pointer transition-colors",
            isDraggingOver
              ? "border-blue-500 bg-blue-50"
              : "border-gray-300 hover:bg-gray-50"
          )}
          onClick={triggerFileInput}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="text-center">
            <Upload
              className={cn(
                "mx-auto h-12 w-12 transition-colors",
                isDraggingOver ? "text-blue-500" : "text-gray-400"
              )}
            />
            <div className="mt-4 flex flex-col items-center gap-2">
              <button
                type="button"
                className="font-medium text-blue-600 hover:text-blue-500"
                onClick={triggerFileInput}
              >
                Upload media
              </button>
              <p className="text-sm text-gray-500">
                {isDraggingOver
                  ? "Drop your files here"
                  : "or drag and drop your files"}
              </p>
              <p className="text-xs text-gray-400">
                Supports images and videos up to 100MB
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  const itemIds = uploadItems.map((item) => item.id);

  return (
    <div className="flex gap-4">
      <div className="flex-1 space-y-4">
        {fileInput}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm text-gray-500">Media Items</Label>
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-400">
                {uploadItems.length}/{MAX_NUM_FILES} items
              </span>
              <TooltipProvider>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger>
                    <Info className="h-3 w-3 text-gray-400 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p className="text-xs max-w-xs">
                      Instagram & LinkedIn only allow 10 media items. Only the
                      first 10 media items will be posted to these platforms.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={itemIds} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-7">
                {uploadItems.map((item, index) => (
                  <SortableUploadItem
                    key={item.id}
                    item={item}
                    isSelected={selectedIndex === index}
                    onSelect={() => onSelectItem(index)}
                    onRemove={() => {
                      const currentIndex = uploadItems.findIndex(
                        (i) => i.id === item.id
                      );
                      if (currentIndex !== -1) {
                        removeUploadItem(item.id);
                        const newLength = uploadItems.length - 1;
                        if (newLength === 0) {
                        } else if (selectedIndex >= currentIndex) {
                          onSelectItem(Math.max(0, selectedIndex - 1));
                        }
                      }
                    }}
                    onReplace={(file) => {
                      onReplaceItem(item.id, file);
                    }}
                    onDuplicate={() => onDuplicateItem(item.id)}
                    onEdit={
                      onEditItem
                        ? () => {
                            onSelectItem(index); // Select the item first
                            onEditItem(index); // Then open editor
                          }
                        : undefined
                    }
                  />
                ))}

                {uploadItems.length < MAX_NUM_FILES && (
                  <button
                    type="button"
                    onClick={triggerFileInput}
                    className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center hover:bg-gray-50"
                  >
                    <Plus className="h-5 w-5 text-gray-400" />
                  </button>
                )}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </div>
  );
}
